/**
 * MONOWHEEL Store Locator — Vanilla JS
 * Replaces: React useState, Framer Motion, Vaul Drawer
 * ================================================
 */

(function () {
  'use strict';

  /* ============================
     STATE
     ============================ */
  const state = {
    stores: [],
    selectedRegion: 'ทุกพื้นที่',
    selectedDemos: [],
    searchQuery: '',
    activeStoreId: null,
    map: null,
    markers: [],
    infoWindow: null,
    searchCoords: null, // Stores {lat, lng} from search query
    userCoords: null,   // Stores actual user location
    nameMatchIds: null, // Set of store ids matching text search (null = no text search active)
  };

  /* ============================
     DOM CACHE
     ============================ */
  const dom = {};

  function cacheDom() {
    dom.searchInput = document.getElementById('sl-search-input');
    dom.searchBtn = document.getElementById('sl-search-btn');
    dom.useLocationBtn = document.getElementById('sl-use-location');
    dom.filtersBar = document.getElementById('sl-filters');
    dom.regionPills = document.getElementById('sl-region-pills');
    dom.storeCards = document.querySelectorAll('.store-card:not(.store-card--mobile)');
    dom.mobileStoreCards = document.querySelectorAll('.store-card--mobile');
    dom.storeCount = document.getElementById('sl-store-count');
    dom.mobileStoreCount = document.getElementById('sl-mobile-store-count');
    dom.noResults = document.getElementById('sl-no-results');
    dom.activeFilters = document.getElementById('sl-active-filters');
    dom.activeTags = document.getElementById('sl-active-tags');
    dom.clearAll = document.getElementById('sl-clear-all');
    dom.clearFilters = document.getElementById('sl-clear-filters');
    dom.clearFiltersMobile = document.getElementById('sl-clear-filters-mobile');
    dom.demoBadge = document.getElementById('sl-demo-badge');
    dom.mobileFilterBtn = document.getElementById('sl-mobile-filter-btn');
    dom.drawerOverlay = document.getElementById('sl-drawer-overlay');
    dom.drawer = document.getElementById('sl-demo-drawer');
    dom.drawerClose = document.getElementById('sl-drawer-close');
    dom.drawerApply = document.getElementById('sl-drawer-apply');
    dom.drawerCount = document.getElementById('sl-drawer-count');
    dom.mobileSheet = document.getElementById('sl-mobile-sheet');
    dom.mapContainer = document.getElementById('sl-google-map');
    dom.zoomIn = document.getElementById('sl-zoom-in');
    dom.zoomOut = document.getElementById('sl-zoom-out');
  }

  /* ============================
     LOAD STORE DATA
     ============================ */
  function loadStoreData() {
    try {
      const dataEl = document.getElementById('sl-store-data');
      if (dataEl) {
        state.stores = JSON.parse(dataEl.textContent);
      }
    } catch (e) {
      console.error('Failed to parse store data:', e);
    }
  }

  /* ============================
     FILTER LOGIC
     ============================ */
  function getFilteredStores() {
    return state.stores.map(function (store) {
      var demos = store.demoProducts.map(function (p) { return p.trim(); });

      var matchRegion =
        state.selectedRegion === 'ทุกพื้นที่' ||
        store.region === state.selectedRegion;

      var matchDemo =
        state.selectedDemos.length === 0 ||
        state.selectedDemos.every(function (d) {
          return demos.indexOf(d) !== -1;
        });

      // Search is location-based: if searchCoords exist, all stores "match" search
      // and are sorted by distance instead of text filtering
      var matchSearch = true;

      store.matchDemo = matchDemo;               // has the selected demo products
      store.isMatch = matchRegion && matchDemo && matchSearch; // full match
      return store;
    });
  }

  function applyFilters() {
    var allStores = getFilteredStores(); // all stores, each tagged with .isMatch

    // Calculate distances for ALL stores if we have search coordinates or user coordinates
    var coords = state.searchCoords || state.userCoords;
    if (coords) {
      allStores.forEach(function (s) {
        s.tempDistance = calculateDistance(
          coords.lat, coords.lng, s.lat, s.lng
        );
      });
    }

    // Sort priority (highest → lowest):
    //  Tier 0: text-name match (when user typed a query matching store name/address) — overrides distance
    //  Tier 1: full filter match (isMatch)
    //  Tier 2: demo match only
    //  Tier 3: no match
    var hasNameMatch = state.nameMatchIds && state.nameMatchIds.size > 0;
    var sorted = allStores.sort(function (a, b) {
      // 0. Name-match priority (top of list when active)
      if (hasNameMatch) {
        var nmA = state.nameMatchIds.has(a.id) ? 0 : 1;
        var nmB = state.nameMatchIds.has(b.id) ? 0 : 1;
        if (nmA !== nmB) return nmA - nmB;
      }
      // 1. Tier priority (match > demo match > no match)
      var rankA = a.isMatch ? 0 : (a.matchDemo ? 1 : 2);
      var rankB = b.isMatch ? 0 : (b.matchDemo ? 1 : 2);
      if (rankA !== rankB) return rankA - rankB;
      // 2. Distance (if available — geolocation or search coords)
      if (coords) return (a.tempDistance || 0) - (b.tempDistance || 0);

      // Default state check: no filter / no name match / no coords
      var isDefaultState = !hasNameMatch
        && state.selectedRegion === 'ทุกพื้นที่'
        && state.selectedDemos.length === 0;

      if (isDefaultState) {
        // ─── DEFAULT ORDER ONLY ─── HQ pin → flagship → L6M desc
        var aHQ = a.metaobjectHandle === 'monowheel-hq-monowheel-hq' ? 0 : 1;
        var bHQ = b.metaobjectHandle === 'monowheel-hq-monowheel-hq' ? 0 : 1;
        if (aHQ !== bHQ) return aHQ - bHQ;
        var aFlag = a.type === 'flagship' ? 0 : 1;
        var bFlag = b.type === 'flagship' ? 0 : 1;
        if (aFlag !== bFlag) return aFlag - bFlag;
        var aL6M = parseFloat(a.l6mRevenue) || 0;
        var bL6M = parseFloat(b.l6mRevenue) || 0;
        if (aL6M !== bL6M) return bL6M - aL6M;
      }

      // 4. Default tiebreaker / filter-active fallback: demo count
      var demosA = (a.demoProducts || []).length;
      var demosB = (b.demoProducts || []).length;
      if (demosA !== demosB) return demosB - demosA;
      return 0;
    });

    var matchCount = sorted.filter(function (s) { return s.isMatch; }).length;
    var demoMatchCount = sorted.filter(function (s) { return s.matchDemo && !s.isMatch; }).length;
    var totalCount = sorted.length;

    // REORDER and SHOW ALL cards in sorted order
    var desktopContainer = document.getElementById('sl-store-cards');
    var mobileGrid = document.getElementById('sl-mobile-store-cards');

    // Remove existing dividers
    document.querySelectorAll('.sl-tier-divider').forEach(function (d) { d.remove(); });

    var hasFilter = state.searchQuery !== '' || state.selectedRegion !== 'ทุกพื้นที่' || state.selectedDemos.length > 0;
    var dividerInserted = { desktop: false, mobile: false };

    sorted.forEach(function (store) {
      var opacity = store.isMatch ? '1' : (store.matchDemo ? '0.75' : '0.45');

      // Insert divider between Tier 1 and Tier 2/3
      if (hasFilter && !store.isMatch && matchCount > 0) {
        if (desktopContainer && !dividerInserted.desktop) {
          desktopContainer.insertAdjacentHTML('beforeend', '<div class="sl-tier-divider"><span>สาขาใกล้เคียง</span></div>');
          dividerInserted.desktop = true;
        }
        if (mobileGrid && !dividerInserted.mobile) {
          mobileGrid.insertAdjacentHTML('beforeend', '<div class="sl-tier-divider"><span>สาขาใกล้เคียง</span></div>');
          dividerInserted.mobile = true;
        }
      }

      if (desktopContainer) {
        var card = desktopContainer.querySelector('.store-card[data-store-id="' + store.id + '"]');
        if (card) {
          card.style.display = '';
          card.style.opacity = opacity;
          desktopContainer.appendChild(card);
        }
      }
      if (mobileGrid) {
        var mCard = mobileGrid.querySelector('.store-card[data-store-id="' + store.id + '"]');
        if (mCard) {
          mCard.style.display = '';
          mCard.style.opacity = opacity;
          mobileGrid.appendChild(mCard);
        }
      }
    });

    // No-results banner (only if zero matches and a filter is active)
    if (dom.noResults) {
      var hasFilter = state.searchQuery !== '' || state.selectedRegion !== 'ทุกพื้นที่' || state.selectedDemos.length > 0;
      if (matchCount === 0 && hasFilter) {
        dom.noResults.innerHTML = '<p style="font-weight:700;margin-bottom:4px">ไม่พบสาขาที่ตรงกับเงื่อนไข</p><p>แสดงสาขาทั้งหมด เรียงตามระยะทาง:</p>';
        dom.noResults.style.display = '';
      } else {
        dom.noResults.style.display = 'none';
      }
    }

    // Highlight matched demo chips
    document.querySelectorAll('.store-card__demo-chip').forEach(function (chip) {
      chip.classList.toggle('store-card__demo-chip--active', state.selectedDemos.indexOf(chip.dataset.demoName) !== -1);
    });

    // Update count label
    var regionText = state.selectedRegion !== 'ทุกพื้นที่' ? 'ใน' + state.selectedRegion : 'ทั่วประเทศ';
    var hasSearch = state.searchQuery !== '' && state.searchCoords;
    var hasNameSearch = state.searchQuery !== '' && hasNameMatch;
    var noFilter = !hasSearch && !hasNameSearch && state.selectedRegion === 'ทุกพื้นที่' && state.selectedDemos.length === 0;
    var countLabel;
    if (hasNameSearch) {
      countLabel = 'พบ ' + state.nameMatchIds.size + ' สาขาตรงชื่อ "' + state.searchQuery + '"';
    } else if (hasSearch) {
      countLabel = 'สาขาใกล้ "' + state.searchQuery + '" — เรียงตามระยะทาง';
    } else if (noFilter) {
      countLabel = 'พบ ' + totalCount + ' สาขา' + regionText;
    } else if (matchCount > 0) {
      countLabel = 'พบ ' + matchCount + ' สาขาที่ตรงกัน (จาก ' + totalCount + ' สาขาทั้งหมด)';
    } else if (demoMatchCount > 0) {
      countLabel = 'ไม่พบสาขาที่ตรงทุกเงื่อนไข • พบ ' + demoMatchCount + ' สาขาที่มีสินค้าทดลองตามที่เลือก (จาก ' + totalCount + ' สาขาทั้งหมด)';
    } else {
      countLabel = 'ไม่พบสาขาที่ตรงกัน • แสดง ' + totalCount + ' สาขาทั้งหมด';
    }
    if (dom.storeCount) dom.storeCount.textContent = countLabel;
    if (dom.mobileStoreCount) dom.mobileStoreCount.textContent = hasSearch ? countLabel : 'พบ ' + matchCount + ' สาขาที่ตรงกัน';
    if (dom.drawerCount) dom.drawerCount.textContent = matchCount;

    // Scroll store list to top when search changes
    if (hasSearch) {
      var listEl = document.getElementById('sl-store-cards');
      if (listEl) listEl.scrollTop = 0;
    }

    updateActiveFilterTags();
    updateMapMarkers(sorted);
    updateDistanceLabels();
  }

  /* ============================
     DISTANCE CALCULATION
     ============================ */
  function calculateDistance(lat1, lon1, lat2, lon2) {
    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2 - lat1);
    var dLon = deg2rad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  function updateDistanceLabels() {
    document.querySelectorAll('.store-card').forEach(function (card) {
      var storeId = parseInt(card.dataset.storeId, 10);
      var store = state.stores.find(function (s) { return s.id === storeId; });
      var distanceEl = card.querySelector('.store-card__distance');

      if (store && store.tempDistance !== undefined && (state.searchCoords || state.userCoords)) {
        if (!distanceEl) {
          var details = card.querySelector('.store-card__details');
          if (details) {
            distanceEl = document.createElement('div');
            distanceEl.className = 'store-card__distance';
            distanceEl.style.fontSize = '12px';
            distanceEl.style.fontWeight = '600';
            distanceEl.style.color = '#3B82F6';
            distanceEl.style.marginTop = '4px';
            details.insertBefore(distanceEl, details.firstChild);
          }
        }
        if (distanceEl) {
          distanceEl.textContent = '📍 ห่างจากคุณ ' + store.tempDistance.toFixed(1) + ' กม.';
          distanceEl.style.display = '';
        }
      } else if (distanceEl) {
        distanceEl.style.display = 'none';
      }
    });
  }

  /* ============================
     ACTIVE FILTER TAGS
     ============================ */
  function updateActiveFilterTags() {
    if (!dom.activeFilters || !dom.activeTags) return;

    if (state.selectedDemos.length === 0) {
      dom.activeFilters.style.display = 'none';
      if (dom.clearFilters) dom.clearFilters.style.display = 'none';
      if (dom.clearFiltersMobile) dom.clearFiltersMobile.style.display = 'none';
      if (dom.demoBadge) dom.demoBadge.style.display = 'none';
      return;
    }

    dom.activeFilters.style.display = '';
    if (dom.clearFilters) dom.clearFilters.style.display = '';
    if (dom.clearFiltersMobile) dom.clearFiltersMobile.style.display = '';
    if (dom.demoBadge) {
      dom.demoBadge.style.display = '';
      dom.demoBadge.textContent = state.selectedDemos.length;
    }

    dom.activeTags.innerHTML = '';
    state.selectedDemos.forEach(function (demo) {
      var tag = document.createElement('button');
      tag.className = 'sl-filter-tag';
      tag.type = 'button';
      tag.innerHTML = demo + ' <span class="sl-filter-tag__remove">&times;</span>';
      tag.addEventListener('click', function () {
        toggleDemo(demo);
      });
      dom.activeTags.appendChild(tag);
    });
  }

  /* ============================
     REGION PILLS
     ============================ */
  function initRegionPills() {
    if (!dom.regionPills) return;
    dom.regionPills.addEventListener('click', function (e) {
      var btn = e.target.closest('.sl-pill');
      if (!btn || !btn.dataset.region) return;

      dom.regionPills.querySelectorAll('.sl-pill').forEach(function (p) {
        p.classList.remove('sl-pill--active');
      });
      btn.classList.add('sl-pill--active');
      state.selectedRegion = btn.dataset.region;
      applyFilters();
    });
  }

  /* ============================
     DEMO PILLS (Desktop)
     ============================ */
  function initDemoPills() {
    // Sort pills by popularity (number of stores that carry each demo product)
    var demoCounts = {};
    state.stores.forEach(function (store) {
      (store.demoProducts || []).forEach(function (p) {
        var name = p.trim();
        demoCounts[name] = (demoCounts[name] || 0) + 1;
      });
    });

    // Keep Liquid-rendered manual order (demo_order array) for both desktop pills and mobile drawer options.
    // No JS reorder — order is intentional per merchandising priorities.

    // Bind click events
    document.querySelectorAll('.sl-pill--demo').forEach(function (pill) {
      pill.addEventListener('click', function () {
        toggleDemo(pill.dataset.demo);
      });
    });
  }

  function toggleDemo(model) {
    var idx = state.selectedDemos.indexOf(model);
    if (idx !== -1) {
      state.selectedDemos.splice(idx, 1);
    } else {
      state.selectedDemos.push(model);
    }
    syncDemoPillStates();
    applyFilters();
  }

  function syncDemoPillStates() {
    // Desktop pills
    document.querySelectorAll('.sl-pill--demo').forEach(function (pill) {
      var isActive = state.selectedDemos.indexOf(pill.dataset.demo) !== -1;
      pill.classList.toggle('sl-pill--active', isActive);
    });

    // Drawer options
    document.querySelectorAll('.sl-drawer__option').forEach(function (opt) {
      var isActive = state.selectedDemos.indexOf(opt.dataset.demo) !== -1;
      opt.classList.toggle('sl-drawer__option--active', isActive);
    });
  }

  /* ============================
     ALL PRODUCTS DROPDOWN (Desktop)
     ============================ */
  function initAllProductsToggle() {
    var btn = document.getElementById('sl-all-products-btn');
    var row = document.getElementById('sl-demos-row');
    if (!btn || !row) return;

    btn.addEventListener('click', function () {
      var isExpanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', !isExpanded);
      row.classList.toggle('sl-filters__demos-row--expanded', !isExpanded);
    });
  }

  function clearAllFilters() {
    state.selectedDemos = [];
    state.selectedRegion = 'ทุกพื้นที่';
    state.searchQuery = '';
    state.searchCoords = null;
    state.nameMatchIds = null;
    if (dom.searchInput) {
      dom.searchInput.value = '';
      var clearBtn = document.getElementById('sl-search-clear');
      if (clearBtn) clearBtn.style.display = 'none';
    }

    if (dom.regionPills) {
      dom.regionPills.querySelectorAll('.sl-pill').forEach(function (p) {
        p.classList.toggle('sl-pill--active', p.dataset.region === 'ทุกพื้นที่');
      });
    }
    syncDemoPillStates();
    applyFilters();

    if (state.map && state.markers.length > 0) {
      var bounds = new google.maps.LatLngBounds();
      state.stores.forEach(function (store) {
        if (store.lat && store.lng) {
          bounds.extend({ lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
        }
      });
      state.map.fitBounds(bounds, { padding: 60 });
    }
  }

  /* ============================
     SEARCH
     ============================ */
  function initSearch() {
    if (!dom.searchInput) return;

    var clearBtn = document.getElementById('sl-search-clear');
    var debounceTimer;

    dom.searchInput.addEventListener('input', function () {
      if (clearBtn) clearBtn.style.display = dom.searchInput.value.trim() ? 'block' : 'none';
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        handleSearch(dom.searchInput.value.trim());
      }, 500);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        dom.searchInput.value = '';
        clearBtn.style.display = 'none';
        handleSearch('');
      });
    }

    if (dom.searchBtn) {
      dom.searchBtn.addEventListener('click', function () {
        handleSearch(dom.searchInput.value.trim());
      });
    }
  }

  /* ============================
     THAI ↔ ENGLISH TRANSLITERATION ALIAS
     ============================
     Map ของชื่อห้าง/พื้นที่/แบรนด์ที่คนค้นบ่อย — Thai ↔ English variants
     เพิ่ม entries ใหม่ได้ตามต้องการ
  */
  var SEARCH_ALIAS = {
    // ── ห้างสรรพสินค้า ──
    'central':     ['เซ็นทรัล', 'เซ็นทรัลพลาซา', 'ภาคกลาง'],
    'centralwo':   ['เซ็นทรัลเวิลด์', 'เซ็นทรัลเวิร์ลด์'],
    'centralworld':['เซ็นทรัลเวิลด์', 'เซ็นทรัลเวิร์ลด์'],
    'siam':        ['สยาม'],
    'paragon':     ['พารากอน'],
    'iconsiam':    ['ไอคอนสยาม', 'ไอคอน สยาม'],
    'emquartier':  ['ดิเอ็มควอเทียร์', 'เอ็มควอเทียร์'],
    'emporium':    ['เอ็มโพเรียม'],
    'emsphere':    ['เอ็มสเฟียร์'],
    'terminal':    ['เทอร์มินอล', 'เทอร์มินัล'],
    'mega':        ['เมกา', 'เมก้า'],
    'fashion':     ['แฟชั่น'],
    'island':      ['ไอส์แลนด์', 'ไอแลนด์'],
    'mall':        ['มอลล์'],
    'festival':    ['เฟสติวัล'],
    'eastville':   ['อีสต์วิลล์', 'อีสวิลล์'],
    'westgate':    ['เวสต์เกต'],
    'westville':   ['เวสต์วิลล์'],
    'robinson':    ['โรบินสัน'],
    'boonthavorn': ['บุญถาวร'],
    'rumo':        ['รูโม่'],

    // ── พื้นที่กรุงเทพฯ ──
    'chidlom':     ['ชิดลม'],
    'ploenchit':   ['เพลินจิต'],
    'asok':        ['อโศก'],
    'asoke':       ['อโศก'],
    'silom':       ['สีลม'],
    'rama':        ['พระราม'],
    'ladprao':     ['ลาดพร้าว'],
    'bangna':      ['บางนา', 'บางนา-ตราด'],
    'rangsit':     ['รังสิต'],
    'ratchapruek': ['ราชพฤกษ์'],
    'changwattana':['แจ้งวัฒนะ'],
    'rama 2':      ['พระราม 2', 'พระราม2'],
    'rama 9':      ['พระราม 9', 'พระราม9'],
    'phra ram':    ['พระราม'],

    // ── จังหวัด ──
    'bangkok':     ['กรุงเทพ', 'บางกอก', 'กทม'],
    'chiang mai':  ['เชียงใหม่'],
    'chiangmai':   ['เชียงใหม่'],
    'chiang rai':  ['เชียงราย'],
    'phuket':      ['ภูเก็ต'],
    'pattaya':     ['พัทยา'],
    'hatyai':      ['หาดใหญ่'],
    'hat yai':     ['หาดใหญ่'],
    'hadyai':      ['หาดใหญ่'],
    'ayutthaya':   ['อยุธยา', 'พระนครศรีอยุธยา'],
    'udon':        ['อุดร', 'อุดรธานี'],
    'khon kaen':   ['ขอนแก่น'],
    'khonkaen':    ['ขอนแก่น'],
    'phitsanulok': ['พิษณุโลก'],
    'saraburi':    ['สระบุรี'],
    'huahin':      ['หัวหิน'],
    'hua hin':     ['หัวหิน'],
    'samui':       ['สมุย', 'เกาะสมุย'],

    // ── แบรนด์ร้าน ──
    'power buy':   ['เพาเวอร์บาย', 'พาวเวอร์บาย'],
    'powerbuy':    ['เพาเวอร์บาย', 'พาวเวอร์บาย'],
    'mi shop':     ['มีช็อป', 'มี ช็อป'],
    'banana':      ['บานาน่า', 'บานาน่า ไอที'],
    'jmart':       ['เจมาร์ท'],
    'comseven':    ['คอมเซเว่น'],
    'monowheel':   ['โมโนวีล'],
    'b play':      ['บีเพลย์', 'บี เพลย์'],
    'bplay':       ['บีเพลย์'],

    // ── ภูมิภาค ──
    'north':       ['ภาคเหนือ'],
    'south':       ['ภาคใต้'],
    'east':        ['ภาคตะวันออก'],
    'west':        ['ภาคตะวันตก'],
    'isan':        ['ภาคอีสาน', 'อีสาน'],
    'northeast':   ['ภาคอีสาน', 'อีสาน']
  };

  /** Expand a query into all variants (lowercase) using SEARCH_ALIAS map */
  function expandSearchVariants(query) {
    var lc = query.toLowerCase().trim();
    var variants = [lc];
    Object.keys(SEARCH_ALIAS).forEach(function (eng) {
      var thaiList = SEARCH_ALIAS[eng];
      // English in query → add Thai variants
      if (lc.indexOf(eng) !== -1) {
        thaiList.forEach(function (t) { variants.push(t.toLowerCase()); });
      }
      // Thai in query → add English variant
      thaiList.forEach(function (t) {
        if (query.indexOf(t) !== -1) variants.push(eng);
      });
    });
    return variants;
  }

  function handleSearch(query) {
    state.searchQuery = query;
    if (!query) {
      state.searchCoords = null;
      state.nameMatchIds = null;
      applyFilters();
      return;
    }

    // 1. Text-match store name/address FIRST (immediate, no API call)
    //    Use Thai ↔ English alias expansion for transliteration matches
    var variants = expandSearchVariants(query);
    var matched = state.stores.filter(function (s) {
      var name = (s.name || '').toLowerCase();
      var addr = (s.address || '').toLowerCase();
      // Match if ANY variant appears in name or address
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v && (name.indexOf(v) !== -1 || addr.indexOf(v) !== -1)) return true;
      }
      return false;
    });
    state.nameMatchIds = new Set(matched.map(function (s) { return s.id; }));

    // Render immediately so name matches appear at top instantly
    applyFilters();

    // 2. Geocode in background (for distance sorting WITHIN tiers + map centering)
    if (window.google && google.maps.Geocoder) {
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query, componentRestrictions: { country: 'TH' } }, function (results, status) {
        if (status === 'OK' && results[0]) {
          applySearchCoords(
            results[0].geometry.location.lat(),
            results[0].geometry.location.lng()
          );
        } else {
          geocodeNominatim(query);
        }
      });
    } else {
      geocodeNominatim(query);
    }
  }

  function geocodeNominatim(query) {
    var url = 'https://nominatim.openstreetmap.org/search?q=' +
      encodeURIComponent(query + ' Thailand') +
      '&format=json&limit=1&countrycodes=th';

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.length > 0) {
          applySearchCoords(parseFloat(data[0].lat), parseFloat(data[0].lon));
        } else {
          state.searchCoords = null;
          applyFilters();
        }
      })
      .catch(function () {
        state.searchCoords = null;
        applyFilters();
      });
  }

  function applySearchCoords(lat, lng) {
    state.searchCoords = { lat: lat, lng: lng };
    applyFilters();
    if (state.map) {
      state.map.setCenter(state.searchCoords);
      state.map.setZoom(11);
    }
  }

  /* ============================
     URL PARAMETERS
     ============================ */
  // "Ninebot KickScooter F3" -> "ninebot-kickscooter-f3" (Thai characters are kept)
  function slugifyDemo(value) {
    return String(value)
      .toLowerCase()
      .replace(/[._+]/g, ' ')
      .replace(/[^a-z0-9฀-๿]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Resolve a link value to the exact demo name used by the pills.
  // Accepts the display name, any casing, or a slug — so both
  // ?demo=Ninebot%20F3 and #ninebot-f3 land on the same filter.
  function resolveDemoName(raw) {
    var want = String(raw || '').trim();
    if (!want) return null;
    var names = Array.prototype.map.call(
      document.querySelectorAll('.sl-pill--demo'),
      function (pill) { return pill.dataset.demo; }
    );
    var i;
    for (i = 0; i < names.length; i++) if (names[i] === want) return names[i];
    for (i = 0; i < names.length; i++) {
      if (names[i].toLowerCase() === want.toLowerCase()) return names[i];
    }
    var slug = slugifyDemo(want);
    for (i = 0; i < names.length; i++) {
      if (slugifyDemo(names[i]) === slug) return names[i];
    }
    return null;
  }

  // Reads the model filter from ?demo= / ?model= or from the hash
  // (#demo=ninebot-f3 and the bare #ninebot-f3 both work). A bare hash is
  // only treated as a filter when nothing on the page owns that id.
  function readDemoParam(params) {
    var raw = params.get('demo') || params.get('model') || '';
    if (raw) return raw;

    var hash = decodeURIComponent((window.location.hash || '').replace(/^#/, ''));
    if (!hash) return '';

    var keyed = hash.match(/^(?:demo|model)[=:-](.+)$/i);
    if (keyed) return keyed[1];

    if (document.getElementById(hash)) return ''; // a real in-page anchor
    return resolveDemoName(hash) ? hash : '';
  }

  function applyDemoDeepLink(raw) {
    var wanted = [];
    String(raw).split(',').forEach(function (part) {
      var name = resolveDemoName(part);
      if (name && wanted.indexOf(name) === -1) wanted.push(name);
    });
    if (!wanted.length) return false;

    state.selectedDemos = wanted;

    // The desktop pill row is collapsed by default — expand it so the
    // selected model is actually visible instead of hidden in the overflow.
    var allBtn = document.getElementById('sl-all-products-btn');
    var demosRow = document.getElementById('sl-demos-row');
    if (allBtn && demosRow) {
      allBtn.setAttribute('aria-expanded', 'true');
      demosRow.classList.add('sl-filters__demos-row--expanded');
    }

    syncDemoPillStates();
    return true;
  }

  function initUrlParams() {
    var params = new URLSearchParams(window.location.search);
    var zipcode = params.get('zipcode');
    if (zipcode && dom.searchInput) {
      dom.searchInput.value = zipcode;
      handleSearch(zipcode);
    }

    var region = params.get('region');
    if (region && dom.regionPills) {
      var regionBtn = dom.regionPills.querySelector('[data-region="' + region + '"]');
      if (regionBtn) {
        state.selectedRegion = region;
        dom.regionPills.querySelectorAll('.sl-pill').forEach(function (p) {
          p.classList.toggle('sl-pill--active', p === regionBtn);
        });
      }
    }

    var branch = params.get('branch');

    var demoRaw = readDemoParam(params);
    if (demoRaw && applyDemoDeepLink(demoRaw) && !branch) {
      // Land the visitor on the filter bar so the applied filter is obvious.
      requestAnimationFrame(function () {
        var target = dom.filtersBar || document.getElementById('sl-split-view');
        if (!target) return;
        var top = target.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    }

    if (branch && state.stores.length > 0) {
      var targetStore = state.stores.find(function(s) { return s.handle === branch; });
      if (targetStore) {
        // Scroll to map area immediately
        var mapEl = document.getElementById('sl-split-view');
        if (mapEl) {
          var offset = mapEl.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top: offset, behavior: 'smooth' });
        }

        // Wait until map AND markers are ready, then trigger marker click
        // (marker click handles both desktop scroll + mobile bottom-sheet snap & scroll)
        var checkMap = setInterval(function() {
          if (state.map && state.markers && state.markers.length > 0) {
            clearInterval(checkMap);
            var marker = state.markers.find(function(m) { return m._storeId === targetStore.id; });
            if (marker && typeof google !== 'undefined' && google.maps) {
              google.maps.event.trigger(marker, 'click');
            } else {
              // Fallback: click the card (desktop card if present, else mobile)
              var card = document.querySelector('.store-card[data-store-id="' + targetStore.id + '"]');
              if (card) {
                card.click();
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }
        }, 100);
        
        // Timeout to stop checking
        setTimeout(function() { clearInterval(checkMap); }, 10000);
      }
    }

    // Anchor links used while already on the page don't reload it, so react
    // to the hash changing too.
    window.addEventListener('hashchange', function () {
      var raw = readDemoParam(new URLSearchParams(window.location.search));
      if (!raw || !applyDemoDeepLink(raw)) return;
      applyFilters();
      var target = dom.filtersBar || document.getElementById('sl-split-view');
      if (!target) return;
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.pageYOffset - 80,
        behavior: 'smooth'
      });
    });
  }

  /* ============================
     GEOLOCATION
     ============================ */
  function initGeolocation() {
    if (!dom.useLocationBtn) return;
    dom.useLocationBtn.addEventListener('click', function () {
      if (!navigator.geolocation) return;
      dom.useLocationBtn.classList.add('sl-geolocation--loading');
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          dom.useLocationBtn.classList.remove('sl-geolocation--loading');
          state.userCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          applyFilters();
          if (state.map) {
            state.map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            state.map.setZoom(13);
          }
        },
        function () {
          dom.useLocationBtn.classList.remove('sl-geolocation--loading');
          alert('ไม่สามารถระบุตำแหน่งได้ กรุณาเปิด Location Services');
        }
      );
    });
  }

  /* ============================
     MOBILE DRAWER (replaces Vaul)
     ============================ */
  function initDrawer() {
    if (dom.mobileFilterBtn) {
      dom.mobileFilterBtn.addEventListener('click', openDrawer);
    }
    if (dom.drawerClose) {
      dom.drawerClose.addEventListener('click', closeDrawer);
    }
    if (dom.drawerOverlay) {
      dom.drawerOverlay.addEventListener('click', closeDrawer);
    }
    if (dom.drawerApply) {
      dom.drawerApply.addEventListener('click', closeDrawer);
    }

    // Drawer demo option clicks
    document.querySelectorAll('.sl-drawer__option').forEach(function (opt) {
      opt.addEventListener('click', function () {
        toggleDemo(opt.dataset.demo);
      });
    });
  }

  function openDrawer() {
    document.body.classList.add('sl-body-locked');
    if (dom.drawerOverlay) dom.drawerOverlay.classList.add('sl-drawer__overlay--open');
    if (dom.drawer) dom.drawer.classList.add('sl-drawer--open');
  }

  function closeDrawer() {
    document.body.classList.remove('sl-body-locked');
    if (dom.drawerOverlay) dom.drawerOverlay.classList.remove('sl-drawer__overlay--open');
    if (dom.drawer) dom.drawer.classList.remove('sl-drawer--open');
  }

  /* ============================
     MOBILE BOTTOM SHEET — 3-snap-state drag
     States: 'collapsed' (peek ~64px) | 'half' (42vh default) | 'expanded' (~85vh)
     ============================ */
  function initMobileSheet() {
    var sheet = dom.mobileSheet;
    if (!sheet) return;
    var inner = document.getElementById('sl-sheet-inner');
    if (!inner) return;
    var handle = sheet.querySelector('.sl-sheet__handle');
    if (!handle) return;

    // Current snap state
    var snapState = 'half'; // 'collapsed' | 'half' | 'expanded'

    // Helper: apply snap state immediately (with transition)
    function applySnap(state, animate) {
      snapState = state;
      inner.style.transition = animate !== false
        ? 'height 0.35s cubic-bezier(0.32,0.72,0,1), transform 0.35s cubic-bezier(0.32,0.72,0,1)'
        : 'none';
      inner.style.transform = '';
      sheet.classList.remove('sl-sheet--collapsed', 'sl-sheet--expanded');
      if (state === 'collapsed') sheet.classList.add('sl-sheet--collapsed');
      if (state === 'expanded') sheet.classList.add('sl-sheet--expanded');
    }

    // Heights for each state (as % of window)
    function snapHeight(state) {
      if (state === 'collapsed') return 64;
      if (state === 'half') return Math.round(window.innerHeight * 0.42);
      if (state === 'expanded') return Math.round(window.innerHeight * 0.85);
    }

    // Drag tracking
    var dragStartY = 0;
    var dragStartHeight = 0;
    var isDragging = false;
    var currentDragY = 0;

    function onDragStart(clientY) {
      isDragging = true;
      dragStartY = clientY;

      // ⚠️ IMPORTANT: remove snap classes BEFORE reading height,
      // otherwise CSS `height: 64px !important` wins over inline style during drag.
      // Pin the current rendered height as inline so JS fully owns size control.
      var currentH = inner.getBoundingClientRect().height;
      sheet.classList.remove('sl-sheet--collapsed', 'sl-sheet--expanded');
      inner.style.height = currentH + 'px';
      inner.style.transition = 'none';
      dragStartHeight = currentH;
    }

    function onDragMove(clientY) {
      if (!isDragging) return;
      currentDragY = clientY - dragStartY;
      // Positive deltaY = finger moved down = shrink sheet
      var newH = Math.max(64, Math.min(window.innerHeight * 0.85, dragStartHeight - currentDragY));
      inner.style.height = newH + 'px';
    }

    function onDragEnd() {
      if (!isDragging) return;
      isDragging = false;

      var finalH = inner.getBoundingClientRect().height;
      var halfH = snapHeight('half');
      var expandH = snapHeight('expanded');
      var collapseH = snapHeight('collapsed');
      var delta = currentDragY; // +ve = dragged down (shrink), -ve = dragged up (expand)
      var THRESHOLD = 60; // px of intentional drag to trigger a step
      var next;

      // ── State-aware: move ±1 step at a time ──
      if (snapState === 'collapsed') {
        // Only direction possible = UP → half
        next = (delta < -THRESHOLD || finalH > collapseH + THRESHOLD) ? 'half' : 'collapsed';

      } else if (snapState === 'half') {
        // Can go UP → expanded, or DOWN → collapsed
        if (delta < -THRESHOLD || finalH > halfH + THRESHOLD) {
          next = 'expanded';
        } else if (delta > THRESHOLD || finalH < halfH - THRESHOLD) {
          next = 'collapsed';
        } else {
          next = 'half';
        }

      } else { // 'expanded'
        // Only direction possible = DOWN → half
        next = (delta > THRESHOLD || finalH < expandH - THRESHOLD) ? 'half' : 'expanded';
      }

      inner.style.height = '';
      applySnap(next, true);
      currentDragY = 0;
    }


    // Touch events on handle
    handle.addEventListener('touchstart', function (e) {
      onDragStart(e.touches[0].clientY);
    }, { passive: true });

    // Also allow dragging from header area
    var header = inner.querySelector('.sl-sheet__header');
    if (header) {
      header.addEventListener('touchstart', function (e) {
        onDragStart(e.touches[0].clientY);
      }, { passive: true });
    }

    document.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      e.preventDefault();
      onDragMove(e.touches[0].clientY);
    }, { passive: false });

    document.addEventListener('touchend', function () {
      onDragEnd();
    }, { passive: true });

    // Tap on handle toggles between half and collapsed
    handle.addEventListener('click', function () {
      if (snapState === 'collapsed') {
        applySnap('half', true);
      } else if (snapState === 'expanded') {
        applySnap('half', true);
      } else {
        applySnap('collapsed', true);
      }
    });

    // Window Resize handling
    window.addEventListener('resize', function () {
      if (snapState === 'expanded') applySnap('expanded', false);
      else if (snapState === 'half') applySnap('half', false);
    });

    // Initial state
    applySnap('half', false);

    // Expose for external use (marker click sync)
    state.applyMobileSnap = applySnap;
  }

  /* ============================
     STICKY FILTER BAR
     ============================ */
  function initStickyFilter() {
    // Replaced by native CSS position: sticky
  }

  /* ============================
     STORE CARD INTERACTIONS
     ============================ */
  function initStoreCardClicks() {
    document.querySelectorAll('.store-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        // Don't trigger on action links
        if (e.target.closest('.store-card__action')) return;

        var storeId = parseInt(card.dataset.storeId, 10);
        state.activeStoreId = storeId;

        // Update active states
        document.querySelectorAll('.store-card').forEach(function (c) {
          c.classList.remove('store-card--active');
        });
        document.querySelectorAll('.store-card[data-store-id="' + storeId + '"]').forEach(function (c) {
          c.classList.add('store-card--active');
        });

        // Pan map to this store
        var store = state.stores.find(function (s) { return s.id === storeId; });
        if (store && state.map) {
          state.map.panTo({ lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
          state.map.setZoom(15);
          // Open info window
          var marker = state.markers.find(function (m) { return m._storeId === storeId; });
          if (marker && state.infoWindow) {
            state.infoWindow.setContent(createInfoWindowContent(store));
            state.infoWindow.open(state.map, marker);
          }
        }
      });
    });
  }

  /* ============================
     CLEAR FILTERS
     ============================ */
  function initClearButtons() {
    [dom.clearAll, dom.clearFilters, dom.clearFiltersMobile].forEach(function (btn) {
      if (btn) btn.addEventListener('click', clearAllFilters);
    });
  }

  /* ============================
     GOOGLE MAPS
     ============================ */
  function createInfoWindowContent(store) {
    var isMobile = window.matchMedia('(max-width: 767px)').matches;
    var closeBtn = isMobile ? '<button onclick="window.__slCloseInfoWindow()" style="position:absolute;top:2px;right:2px;background:none;border:none;font-size:18px;color:#9CA3AF;cursor:pointer;padding:4px;line-height:1">&times;</button>' : '';
    return (
      '<div style="font-family:IBM Plex Sans Thai,sans-serif;padding:4px;min-width:200px;position:relative">' +
      closeBtn +
      '<strong style="font-size:14px;font-weight:600">' + store.name + '</strong><br>' +
      '<span style="color:#6B7280;font-size:12px">' + store.address + '</span><br>' +
      '<div style="margin-top:8px;display:flex;gap:12px">' +
      '<a href="https://www.google.com/maps/dir/?api=1&destination=' + store.lat + ',' + store.lng + '" target="_blank" style="color:#1A56DB;font-size:12px;font-weight:500;text-decoration:none">นำทาง</a>' +
      (store.phone ? '<a href="tel:' + store.phone + '" style="color:#1A56DB;font-size:12px;font-weight:500;text-decoration:none">โทร</a>' : '') +
      '</div>' +
      '</div>'
    );
  }

  function updateMapMarkers(sorted) {
    if (!state.map) return;

    state.markers.forEach(function (marker) {
      var store = sorted.find(function (s) { return s.id === marker._storeId; });
      if (store) {
        marker.setOpacity(store.isMatch ? 1.0 : 0.4);
        marker.setZIndex(store.isMatch ? 100 : 1);
        marker.setVisible(true); // Always visible
      }
    });

    // Fit bounds around matching stores only
    var matchingStores = sorted.filter(function (s) { return s.isMatch; });
    var storesToBound = matchingStores.length > 0 ? matchingStores : sorted;
    if (storesToBound.length > 0 && storesToBound.length < state.stores.length) {
      var bounds = new google.maps.LatLngBounds();
      storesToBound.forEach(function (store) {
        bounds.extend({ lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
      });
      state.map.fitBounds(bounds, { padding: 60 });
    }
  }

  // Called by Google Maps script callback
  window.initStoreLocatorMap = function () {
    if (!dom.mapContainer || state.stores.length === 0) return;

    var defaultLat = parseFloat(dom.mapContainer.closest('[data-section-id]')?.querySelector('[data-default-lat]')?.dataset.defaultLat || '13.7563');
    var defaultLng = parseFloat(dom.mapContainer.closest('[data-section-id]')?.querySelector('[data-default-lng]')?.dataset.defaultLng || '100.5018');

    var customStyles = [];
    try {
      var styleAttr = dom.mapContainer.dataset.mapStyle;
      if (styleAttr) {
        customStyles = JSON.parse(styleAttr);
      }
    } catch (e) {
      console.error('Failed to parse custom map style:', e);
    }

    state.map = new google.maps.Map(dom.mapContainer, {
      center: { lat: defaultLat, lng: defaultLng },
      zoom: 6,
      styles: customStyles.length > 0 ? customStyles : mapStyles,
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    state.infoWindow = new google.maps.InfoWindow();

    // Global close function for mobile X button
    window.__slCloseInfoWindow = function() {
      if (state.infoWindow) state.infoWindow.close();
    };

    // Create markers
    state.stores.forEach(function (store) {
      var icon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#1A56DB',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 3,
      };

      if (store.mapPinUrl) {
        icon = {
          url: store.mapPinUrl,
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 20)
        };
      }

      var marker = new google.maps.Marker({
        position: { lat: parseFloat(store.lat), lng: parseFloat(store.lng) },
        map: state.map,
        title: store.name,
        icon: icon,
      });
      marker._storeId = store.id;

      marker.addListener('click', function () {
        state.infoWindow.setContent(createInfoWindowContent(store));
        state.infoWindow.open(state.map, marker);
        state.activeStoreId = store.id;

        var isMobile = window.matchMedia('(max-width: 767px)').matches;

        // Sync active classes on ALL cards (desktop + mobile)
        document.querySelectorAll('.store-card').forEach(function (c) {
          var isMatch = parseInt(c.dataset.storeId, 10) === store.id;
          c.classList.toggle('store-card--active', isMatch);
          c.classList.toggle('active', isMatch);
        });

        if (isMobile) {
          // Mobile: open sheet to half, scroll mobile card container
          if (state.applyMobileSnap) state.applyMobileSnap('half', true);
          var mobileCard = document.querySelector('.store-card--mobile[data-store-id="' + store.id + '"]');
          var mobileContainer = document.getElementById('sl-mobile-store-cards');
          if (mobileCard && mobileContainer) {
            setTimeout(function() {
              mobileContainer.scrollTop = mobileCard.offsetTop - mobileContainer.offsetTop;
            }, 400);
          }
        } else {
          // Desktop: scroll desktop store list container
          var desktopCard = document.querySelector('#sl-store-cards .store-card[data-store-id="' + store.id + '"]');
          var desktopContainer = document.getElementById('sl-store-cards');
          if (desktopCard && desktopContainer) {
            desktopCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      });

      state.markers.push(marker);
    });

    // Custom zoom controls
    if (dom.zoomIn) {
      dom.zoomIn.addEventListener('click', function () { state.map.setZoom(state.map.getZoom() + 1); });
    }
    if (dom.zoomOut) {
      dom.zoomOut.addEventListener('click', function () { state.map.setZoom(state.map.getZoom() - 1); });
    }

    // Apply pending user location if geolocation resolved before map init
    var params = new URLSearchParams(window.location.search);
    var hasBranch = !!params.get('branch');
    if (state._pendingUserCoords && state.userCoords && !hasBranch) {
      state._pendingUserCoords = false;
      applyFilters();
      state.map.setCenter(state.userCoords);
      state.map.setZoom(13);
    }
  };

  /* ============================
     LEAFLET FALLBACK (OpenStreetMap)
     ============================ */
  window.initStoreLocatorMapFallback = function () {
    initLeafletFallback();
  };

  function initLeafletFallback() {
    if (!dom.mapContainer || state.stores.length === 0) return;

    // Load Leaflet CSS
    var cssLink = document.getElementById('sl-leaflet-css');
    if (cssLink && cssLink.rel === 'preload') {
      cssLink.rel = 'stylesheet';
    }

    // Load Leaflet JS then init map
    var scriptEl = document.getElementById('sl-leaflet-js');
    if (!scriptEl) return;

    function boot() {
      if (!window.L) return;

      var map = L.map(dom.mapContainer, {
        zoomControl: false
      }).setView([13.7563, 100.5018], 6);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
      }).addTo(map);

      L.control.zoom({ position: 'bottomright' }).addTo(map);

      // Create markers
      var bounds = L.latLngBounds();
      state.stores.forEach(function (store) {
        if (!store.lat || !store.lng) return;
        var lat = parseFloat(store.lat);
        var lng = parseFloat(store.lng);

        var marker = L.circleMarker([lat, lng], {
          radius: 8,
          fillColor: '#1A56DB',
          fillOpacity: 1,
          color: '#FFFFFF',
          weight: 2
        }).addTo(map);

        marker.bindPopup(
          '<strong>' + store.name + '</strong><br>' +
          '<span style="color:#6B7280;font-size:12px">' + store.address + '</span><br>' +
          '<a href="https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng + '" target="_blank" style="color:#1A56DB;font-size:12px;font-weight:700">นำทาง</a>'
        );

        marker._storeId = store.id;
        bounds.extend([lat, lng]);
      });

      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });

      // Wire up zoom controls
      if (dom.zoomIn) {
        dom.zoomIn.addEventListener('click', function () { map.zoomIn(); });
      }
      if (dom.zoomOut) {
        dom.zoomOut.addEventListener('click', function () { map.zoomOut(); });
      }
    }

    // Load script dynamically
    var src = scriptEl.dataset.src || scriptEl.src;
    if (window.L) {
      boot();
    } else {
      var s = document.createElement('script');
      s.src = src;
      s.onload = boot;
      document.head.appendChild(s);
    }
  }

  // Minimal map style (clean gray)
  var mapStyles = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ];

  /* ============================
     OPEN/CLOSED STATUS (real-time)
     ============================ */
  function parseHoursRange(hoursStr) {
    if (!hoursStr) return null;
    var match = hoursStr.match(/(\d{1,2})[:.:](\d{2})\s*[–\-]\s*(\d{1,2})[:.:](\d{2})/);
    if (!match) return null;
    return {
      openH: parseInt(match[1], 10),
      openM: parseInt(match[2], 10),
      closeH: parseInt(match[3], 10),
      closeM: parseInt(match[4], 10)
    };
  }

  function updateOpenClosedStatus() {
    // Feature removed per user request
  }

  function initOpenClosedStatus() {
    // Feature removed per user request
  }

  /* ============================
     SCROLL REVEAL ANIMATION
     (replaces Framer Motion initial/animate)
     ============================ */
  function initScrollReveal() {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('sl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.sl-link-card, .store-card').forEach(function (el) {
      observer.observe(el);
    });
  }

  function requestLocation() {
    if (!navigator.geolocation) return;

    var params = new URLSearchParams(window.location.search);
    var hasBranch = !!params.get('branch');

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        state.userCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        applyFilters();
        
        // Only center map on user if they didn't land on a specific branch
        if (!hasBranch) {
          if (state.map) {
            state.map.setCenter(state.userCoords);
            state.map.setZoom(13);
          } else {
            state._pendingUserCoords = true;
          }
        }
      },
      function () {
        // Silent fail on proactive request
      }
    );
  }

  /* ============================
     INIT
     ============================ */
  function init() {
    cacheDom();
    loadStoreData();
    initRegionPills();
    initDemoPills();
    initAllProductsToggle();
    initSearch();
    initGeolocation();
    initDrawer();
    initMobileSheet();
    initStickyFilter();
    initStoreCardClicks();
    initClearButtons();
    initScrollReveal();
    initOpenClosedStatus();
    initUrlParams();

    // Apply default sort on first load (HQ pin → flagship → L6M desc)
    applyFilters();

    // Proactive location request
    requestLocation();

    // Fallback: wait for Google Maps, then fall back to Leaflet if it fails
    if (!window.google && dom.mapContainer) {
      var fallbackTimeout = setTimeout(function () {
        if (!state.map) initLeafletFallback();
      }, 5000);

      // If Google Maps loads later, cancel fallback
      var origInit = window.initStoreLocatorMap;
      window.initStoreLocatorMap = function () {
        clearTimeout(fallbackTimeout);
        origInit();
      };
    }
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
